/**
 * WalkinRegisterPanel
 *
 * Admin → "Walk-ins" tab. Shows the WhatsApp-fed walk-in log with date
 * filter + free-text search + per-row edit/delete + a one-click Excel
 * export. Admins and managers both have access (per user spec).
 *
 * Audio playback: pending_review entries (transcription failed)
 * surface the original voice note. We fetch it as an authenticated blob
 * and assign a local object URL so the <audio> element doesn't need to
 * send the JWT itself.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Download, Search, Trash2, Pencil, Users, MessageCircle, Play, Loader2 } from 'lucide-react';
import { api } from '../../lib/store';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WalkinAudioPlayer
 *
 * Loads the voice note as a blob (so we can attach the JWT header on the
 * fetch call) then hands the resulting object-URL to a native <audio>
 * control. Avoids any server-side query-param token gymnastics.
 */
function WalkinAudioPlayer({ walkinId }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const objectUrlRef = useRef('');

  useEffect(() => () => {
    // Revoke the blob URL when the component unmounts to free memory.
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const load = async () => {
    if (url) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('heda_token');
      const res = await axios.get(`${API_BASE}/walkins/${walkinId}/audio`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const objectUrl = URL.createObjectURL(res.data);
      objectUrlRef.current = objectUrl;
      setUrl(objectUrl);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load audio');
    } finally {
      setLoading(false);
    }
  };

  if (!url) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold mt-1 h-8 px-3 text-[11px]"
        onClick={load}
        disabled={loading}
        data-testid={`walkin-audio-load-${walkinId}`}
      >
        <Play className="w-3 h-3 mr-1" /> {loading ? 'Loading…' : 'Play voice'}
      </Button>
    );
  }
  return <audio controls src={url} className="mt-1 h-7 w-44 block" data-testid={`walkin-audio-${walkinId}`} />;
}

/**
 * AttendingStaffEditor
 *
 * Lightweight multi-select chips for the walk-in edit dialog. Lets the
 * admin add/remove tagged staff so the WhatsApp follow-up reminder
 * fans out to every tagged user — without bloating the parent component
 * with another search/dropdown loop.
 */
function AttendingStaffEditor({ value, onChange, allStaff }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selectedIds = new Set((value || []).map((s) => s.user_id));
  const q = query.trim().toLowerCase();
  const suggestions = allStaff
    .filter((s) => !selectedIds.has(s.id))
    .filter((s) => !q ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.staff_code || '').toLowerCase().includes(q))
    .slice(0, 10);

  const add = (s) => {
    if (!s?.id) return;
    onChange([...(value || []), {
      user_id: s.id,
      name:    s.name || '',
      phone:   s.phone || '',
      role:    s.role || '',
    }]);
    setQuery('');
    setOpen(false);
  };
  const remove = (userId) => {
    onChange((value || []).filter((s) => s.user_id !== userId));
  };

  return (
    <div>
      <div
        className="mt-1 min-h-[40px] border border-heda-line rounded-xl px-2 py-1.5 flex flex-wrap gap-1 items-center bg-heda-cream/50 focus-within:border-heda-gold"
        data-testid="walkin-edit-attending-staff"
      >
        {(value || []).map((s) => (
          <span
            key={s.user_id || s.name}
            className="inline-flex items-center gap-1 bg-heda-olive-tint text-heda-olive-dark text-[11px] px-2 py-0.5 rounded-full"
          >
            {s.name || 'Unnamed'}
            <button
              type="button"
              onClick={() => remove(s.user_id)}
              className="ml-0.5 text-heda-mute hover:text-red-700"
              aria-label={`Remove ${s.name}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={(value || []).length === 0 ? 'Add staff…' : 'Add more…'}
          className="flex-1 min-w-[100px] text-base md:text-xs outline-none bg-transparent"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="relative">
          <div className="absolute z-20 mt-1 w-full bg-white border border-heda-line rounded-xl shadow-heda max-h-44 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => add(s)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-heda-cream flex items-center justify-between"
              >
                <span>{s.name || 'Unnamed'} <span className="text-heda-mute">({s.role})</span></span>
                {s.staff_code && <span className="text-[11px] text-heda-mute">{s.staff_code}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS = {
  customer_name:  'Customer Name',
  customer_phone: 'Phone',
  location:       'Location',
  visit_date:     'Visit Date',
  products_shown: 'Products',
  rate_quoted:    'Rate Quoted',
  quantity:       'Quantity',
  follow_up_date: 'Follow-up Date',
  follow_up_time: 'Follow-up Time (24h, e.g. 17:00)',
  architect_name: 'Architect Name',
  carpenter_name: 'Carpenter Name',
  notes:          'Notes',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function WalkinRegisterPanel() {
  const [walkins, setWalkins] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Visit-date range filter (created_at "logged" range removed per business
  // request — admins only care about when the customer actually visited,
  // not when the record was back-filled into the system).
  const [visitFrom, setVisitFrom] = useState('');
  const [visitTo, setVisitTo]   = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // walkin doc OR null
  // Staff list — fetched once per mount and reused in the edit dialog's
  // multi-select. Keeping it at the panel level avoids re-fetching on
  // every edit click.
  const [allStaff, setAllStaff] = useState([]);

  useEffect(() => {
    api.getSalespeople()
      .then((res) => setAllStaff(res.data?.salespeople || []))
      .catch(() => setAllStaff([]));
  }, []);

  const params = () => {
    const p = {};
    if (visitFrom) p.visit_date_from = visitFrom;
    if (visitTo) p.visit_date_to = visitTo;
    if (search.trim()) p.search = search.trim();
    return p;
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.listWalkins(params());
      setWalkins(res.data?.walkins || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load walk-ins');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh on date/search debounce — but only refresh on enter or
  // blur for the search input to avoid spamming.
  // Re-fetch when filters change. queueMicrotask defers state setters to
  // satisfy `react-hooks/set-state-in-effect` on React 19.
  useEffect(() => {
    let cancelled = false;
     
    queueMicrotask(() => { if (!cancelled) refresh(); });
    return () => { cancelled = true; };
  }, [visitFrom, visitTo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    try {
      const res = await api.exportWalkinsXlsx(params());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `walkin_register_${today()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Excel export failed');
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete walk-in for "${w.customer_name || 'this customer'}"? This cannot be undone.`)) return;
    try {
      await api.deleteWalkin(w.id);
      toast.success('Walk-in deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-walkin-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-heda-gold" />
          <h2 className="font-display text-[17px] sm:text-[19px] font-bold text-heda-ink">Walk-in Register</h2>
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
            <MessageCircle className="w-3 h-3 inline-block mr-1" /> WhatsApp fed
          </span>
        </div>
        <Button
          onClick={exportXlsx}
          className="h-10 rounded-xl bg-heda-olive px-4 font-semibold text-white shadow-none hover:bg-heda-olive-dark"
          data-testid="walkin-export-btn"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Visit From</Label>
            <Input type="date" value={visitFrom} onChange={e => setVisitFrom(e.target.value)} className="h-10 rounded-xl mt-1 bg-heda-cream/50" data-testid="walkin-filter-visit-from" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Visit To</Label>
            <Input type="date" value={visitTo} onChange={e => setVisitTo(e.target.value)} className="h-10 rounded-xl mt-1 bg-heda-cream/50" data-testid="walkin-filter-visit-to" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') refresh(); }}
                onBlur={refresh}
                placeholder="Customer, phone, product, salesperson, location, staff, notes…"
                className="h-10 rounded-xl pl-9 bg-heda-cream/50"
                data-testid="walkin-filter-search"
              />
            </div>
          </div>
          {(visitFrom || visitTo) && (
            <div className="sm:col-span-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setVisitFrom(''); setVisitTo(''); }}
                className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[12px] h-8"
                data-testid="walkin-filter-clear-visit"
              >
                Clear Visit-Date Filter
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <p className="flex items-center justify-center gap-2 text-sm text-heda-mute py-10"><Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading…</p>
          ) : walkins.length === 0 ? (
            <p className="text-sm text-heda-mute py-10 text-center">No walk-ins match the current filters.</p>
          ) : (
            <>
              <p className="heda-label border-b border-heda-line px-5 py-3 !mb-0"><span className="text-heda-ink">{total}</span> walk-in{total === 1 ? '' : 's'} found</p>
              {(() => {
                // Compute per-render which of Rate / Qty / Architect
                // columns have ANY value in the current result set —
                // entirely-empty columns are hidden (header + all
                // cells) so the table doesn't waste horizontal space.
                const showRate = walkins.some((w) => (w.rate_quoted || '').toString().trim() !== '');
                const showQty  = walkins.some((w) => (w.quantity || '').toString().trim() !== '');
                const showArch = walkins.some((w) => (w.architect_name || '').toString().trim() !== '');
                return (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="heda-thead">
                    <tr>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Date / Time</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Salesperson</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden xl:table-cell">Attended By</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Customer</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Phone</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Visit Date</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden 2xl:table-cell">Location</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Products</th>
                      {showRate && <th className="px-3 sm:px-4 py-2.5 font-semibold hidden xl:table-cell">Rate</th>}
                      {showQty  && <th className="px-3 sm:px-4 py-2.5 font-semibold hidden xl:table-cell">Qty</th>}
                      {showArch && <th className="px-3 sm:px-4 py-2.5 font-semibold hidden xl:table-cell">Architect</th>}
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden 2xl:table-cell">Carpenter</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Follow-up</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold text-right w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walkins.map(w => {
                      const isPending = w.status === 'pending_review';
                      return (
                      <tr
                        key={w.id}
                        className={`border-t border-heda-line hover:bg-heda-cream/50 ${isPending ? 'bg-amber-50' : ''}`}
                        data-testid={`walkin-row-${w.id}`}
                      >
                        <td className="px-3 sm:px-4 py-3 tabular-nums text-xs text-heda-sub whitespace-nowrap hidden lg:table-cell">
                          {(w.created_at || '').replace('T',' ').slice(0,16)}
                          {isPending && (
                            <span className="block text-[10px] uppercase tracking-wider text-amber-700 font-bold mt-0.5">
                              ⚠ Pending review
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                          <span className="font-medium text-heda-ink">{w.salesperson_name || '—'}</span>
                          {w.salesperson_staff_code && <span className="ml-1 rounded-full bg-heda-olive-tint px-1.5 py-0.5 text-[10px] font-bold text-heda-olive-dark">{w.salesperson_staff_code}</span>}
                          {w.audio_filename && <WalkinAudioPlayer walkinId={w.id} />}
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden xl:table-cell" data-testid={`walkin-attending-staff-${w.id}`}>
                          {Array.isArray(w.attending_staff) && w.attending_staff.length > 0 ? (() => {
                            // Show up to 2 pills inline; anything beyond
                            // collapses into a single "+N" pill whose
                            // title lists every remaining name so hover
                            // reveals the full attendee list.
                            const shown = w.attending_staff.slice(0, 2);
                            const rest  = w.attending_staff.slice(2);
                            const fullList = w.attending_staff.map((s) => s.name || 'Unnamed').join(', ');
                            return (
                              <div className="flex flex-wrap gap-1 max-w-[170px]" title={fullList}>
                                {shown.map((s) => (
                                  <span
                                    key={s.user_id || s.name}
                                    className="inline-block bg-heda-olive-tint text-heda-olive-dark text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                    title={s.phone || ''}
                                  >
                                    {s.name || 'Unnamed'}
                                  </span>
                                ))}
                                {rest.length > 0 && (
                                  <span
                                    className="inline-block bg-heda-cream text-heda-mute text-[10px] px-2 py-0.5 rounded-full font-bold"
                                    title={rest.map((s) => s.name || 'Unnamed').join(', ')}
                                  >
                                    +{rest.length}
                                  </span>
                                )}
                              </div>
                            );
                          })() : (
                            <span className="text-heda-mute">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="font-medium text-heda-ink">{w.customer_name || '—'}</div>
                          <div className="text-[11px] text-heda-mute tabular-nums md:hidden">
                            {w.customer_phone || '—'}
                            {isPending && <span className="ml-1.5 font-bold uppercase text-amber-700">⚠ Pending review</span>}
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 tabular-nums text-heda-sub hidden md:table-cell">{w.customer_phone || '—'}</td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-heda-sub tabular-nums hidden sm:table-cell" data-testid={`walkin-visit-date-${w.id}`}>{w.visit_date || (w.created_at ? String(w.created_at).slice(0, 10) : '—')}</td>
                        <td className="px-3 sm:px-4 py-3 text-heda-sub hidden 2xl:table-cell" data-testid={`walkin-location-${w.id}`}>{w.location || '—'}</td>
                        <td className="px-3 sm:px-4 py-3 max-w-[160px] truncate hidden lg:table-cell" title={w.products_shown}>{w.products_shown || '—'}</td>
                        {showRate && <td className="px-3 sm:px-4 py-3 whitespace-nowrap hidden xl:table-cell">{w.rate_quoted || '—'}</td>}
                        {showQty  && <td className="px-3 sm:px-4 py-3 whitespace-nowrap hidden xl:table-cell">{w.quantity || '—'}</td>}
                        {showArch && <td className="px-3 sm:px-4 py-3 text-heda-sub hidden xl:table-cell" data-testid={`walkin-architect-${w.id}`}>{w.architect_name || '—'}</td>}
                        <td className="px-3 sm:px-4 py-3 text-heda-sub hidden 2xl:table-cell" data-testid={`walkin-carpenter-${w.id}`}>{w.carpenter_name || '—'}</td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap hidden md:table-cell">
                          {w.follow_up_date || '—'}
                          {w.follow_up_time && <span className="ml-1 text-[11px] text-heda-sub">@ {w.follow_up_time}</span>}
                          {w.follow_up_reminder_sent && (
                            <span
                              className="ml-1 inline-flex items-center text-[10px] font-semibold bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5"
                              title={w.reminder_sent_at ? `Reminder sent ${w.reminder_sent_at.slice(0,16).replace('T',' ')} IST` : 'Reminder sent'}
                            >
                              🔔 sent
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="rounded-full hover:bg-heda-cream h-9 w-9 p-0" onClick={() => setEditing(w)} title={isPending ? 'Listen + fill details' : 'Edit'} data-testid={`walkin-edit-${w.id}`}>
                            <Pencil className="w-4 h-4 text-heda-ink" />
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-full hover:bg-red-50 h-9 w-9 p-0" onClick={() => remove(w)} title="Delete" data-testid={`walkin-delete-${w.id}`}>
                            <Trash2 className="w-4 h-4 text-red-700" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              ); })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="walkin-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit walk-in</DialogTitle>
            <DialogDescription className="text-[13px] text-heda-sub">
              Logged via {editing?.source || 'whatsapp'} on {(editing?.created_at || '').replace('T',' ').slice(0,16)}.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {editing.audio_filename && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Original Voice Note</Label>
                  <WalkinAudioPlayer walkinId={editing.id} />
                  {editing.failure_reason && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      Auto-transcription failed: <code>{editing.failure_reason}</code>
                    </p>
                  )}
                </div>
              )}
              {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">{label}</Label>
                  {key === 'notes' ? (
                    <Textarea
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      rows={3}
                      className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
                      data-testid={`walkin-edit-${key}`}
                    />
                  ) : key === 'follow_up_time' ? (
                    <Input
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      className="rounded-xl mt-1 text-base md:text-sm tabular-nums bg-heda-cream/50"
                      type="time"
                      data-testid={`walkin-edit-${key}`}
                    />
                  ) : (
                    <Input
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
                      type={key === 'follow_up_date' ? 'date' : 'text'}
                      data-testid={`walkin-edit-${key}`}
                    />
                  )}
                </div>
              ))}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Attended By</Label>
                <AttendingStaffEditor
                  value={editing.attending_staff || []}
                  onChange={(arr) => setEditing({ ...editing, attending_staff: arr })}
                  allStaff={allStaff}
                />
                <p className="text-[11px] text-heda-mute mt-1">
                  Each tagged staff gets the WhatsApp follow-up reminder on the follow-up date.
                </p>
              </div>
              {editing.transcript && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Original transcript</Label>
                  <p className="mt-1 text-[12px] italic text-heda-sub bg-heda-cream/50 border border-heda-line rounded-xl p-3 whitespace-pre-wrap">
                    {editing.transcript}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-heda-line">
                <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button
                  className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black"
                  onClick={async () => {
                    try {
                      await api.updateWalkin(editing.id, {
                        customer_name:  editing.customer_name  || '',
                        customer_phone: editing.customer_phone || '',
                        location:       editing.location       || '',
                        visit_date:     editing.visit_date     || '',
                        products_shown: editing.products_shown || '',
                        rate_quoted:    editing.rate_quoted    || '',
                        quantity:       editing.quantity       || '',
                        follow_up_date: editing.follow_up_date || '',
                        follow_up_time: editing.follow_up_time || '',
                        architect_name: editing.architect_name || '',
                        carpenter_name: editing.carpenter_name || '',
                        notes:          editing.notes          || '',
                        attending_staff: editing.attending_staff || [],
                      });
                      toast.success('Walk-in updated');
                      setEditing(null);
                      refresh();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Update failed');
                    }
                  }}
                  data-testid="walkin-edit-save"
                >Save changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
